import { useEffect } from "react";
import { logout } from "src/redux/slices/userDetails";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import { userActivityKey, getUserSession } from "src/utils/userSession";
import { recordUserActivity, signOut } from "src/utils/api/userSessions";
import { ACCESS_TOKEN } from "src/utils/common";

const idleMilliseconds = 20 * 60 * 1000;

export const useUserSession = () => {
  const loggedIn = useAppSelector((state) => state.root.user.isLoggedIn);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const session = getUserSession(localStorage.getItem(ACCESS_TOKEN));
    if (!loggedIn || !session) return;
    const key = userActivityKey(session.sessionId);
    let lastHeartbeat = 0;
    let ending = false;

    const end = () => {
      if (ending) return;
      ending = true;
      // signOut captures its credential; a stalled network must not delay local expiry.
      void signOut();
      dispatch(logout());
    };
    const check = () => {
      if (!localStorage.getItem(ACCESS_TOKEN)) {
        dispatch(logout());
        return;
      }
      const lastActivity = Number(localStorage.getItem(key));
      if (
        !lastActivity ||
        Date.now() - lastActivity >= idleMilliseconds ||
        Date.now() >= session.sessionExpiresAt
      )
        end();
    };
    const activity = () => {
      if (ending || document.visibilityState === "hidden") return;
      const now = Date.now();
      const lastActivity = Number(localStorage.getItem(key));
      // An interaction after expiry must not revive the old browser session.
      if (
        !lastActivity ||
        now - lastActivity >= idleMilliseconds ||
        now >= session.sessionExpiresAt
      ) {
        end();
        return;
      }
      if (now - lastActivity >= 1000) localStorage.setItem(key, String(now));
      if (now - lastHeartbeat >= 60 * 1000) {
        lastHeartbeat = now;
        void recordUserActivity().catch(() => undefined);
      }
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === ACCESS_TOKEN || event.key === null) {
        const next = getUserSession(localStorage.getItem(ACCESS_TOKEN));
        if (!next) {
          dispatch(logout());
          return;
        } else if (next.sessionId !== session.sessionId) {
          window.location.reload();
          return;
        }
      }
      check();
    };
    const events = [
      "pointerdown",
      "pointermove",
      "keydown",
      "scroll",
      "touchstart",
    ];
    events.forEach((name) =>
      window.addEventListener(name, activity, { passive: true })
    );
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", check);
    const timer = window.setInterval(check, 1000);
    check();
    return () => {
      window.clearInterval(timer);
      events.forEach((name) => window.removeEventListener(name, activity));
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", check);
    };
  }, [loggedIn, dispatch]);
};
